import { Form, Formik } from 'formik';
import { isNull, omit, omitBy } from 'lodash';
import DocumentTitle from 'react-document-title';

import { Container } from '@hivebrite/design-system/Container';
import { useMatchBreakpoint } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useJobContext } from 'hb-react/shared/apps/JobForm/contexts/JobContext';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { IsActive } from 'hb-react/shared/components/IsActive';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

import { Actions } from '../components/Actions';
import { Apply } from '../components/Apply';
import { CreatedFrom } from '../components/CreatedFrom';
import { Custom } from '../components/Custom';
import { Description } from '../components/Description';
import { GeneralInfo } from '../components/GeneralInfo';
import { Header } from '../components/Header';
import { OtherInfo } from '../components/OtherInfo';
import { PublicationInfo } from '../components/PublicationInfo';
import { Recruiter } from '../components/Recruiter';
import { Section } from '../components/Section';
import { Uploads } from '../components/Uploads';
import { MODE } from '../constants';
import type { ModeValues } from '../types';
import { onSubmit } from '../utils/onSubmit';

interface JobFormContainerProps {
  configPage?: { menu_name: string };
  mode: ModeValues;
  onSuccess?: (companyId?: number) => void;
}

const JobFormContainer = ({
  configPage,
  mode,
  onSuccess,
}: JobFormContainerProps) => {
  const { currentNetwork } = useDefaultAppContexts();

  const { flagsLoaded } = useFeatureFlag();
  const isMobile = useMatchBreakpoint(['s']);
  const isBackofficeMode = mode === MODE.BO;
  const { pathname } = window.location;

  const {
    currentUserId,
    customAttributesEnabled,
    allowLinkToOrg,
    infoBox,
    job,
    navigate,
    networkId,
    topicId,
  } = useJobContext();

  if (!job || !flagsLoaded) {
    return null;
  }

  const getPageTitle = () => {
    if (/\/new$/i.test(pathname)) {
      return `${__('New')} ${configPage?.menu_name || __('Opportunities')}`;
    }
    if (/\/edit$/i.test(pathname)) {
      return `${__('Edit')} ${job.title}`;
    }

    return `${__('Renew')} ${job.title}`;
  };

  return (
    <>
      {!isBackofficeMode ? (
        <DocumentTitle
          title={generatePageTitle({
            title: getPageTitle(),
            networkName: getPageTitleFromNetwork(currentNetwork),
          })}
        />
      ) : null}
      <Formik
        initialValues={{
          ...omit(job, ['captcha_options', 'currency', 'locations']),
          location: { lat: '', ...omitBy(job?.location, isNull) },
          captcha_key: job?.captcha_options?.field_value,
          currency: job?.currency?.id,
          token: job?.edit_token,
        }}
        onSubmit={onSubmit({
          currentUserId,
          mode,
          navigate,
          networkId,
          topicId,
          onSuccess,
        })}
      >
        <Form>
          <IsActive isActive={isBackofficeMode}>
            <Header job={job} networkId={networkId} topicId={topicId} />
          </IsActive>
          <Container>
            <Grid width="100%" mx={isMobile ? 0 : undefined}>
              <Row>
                <Col
                  offset={{
                    _: 0,
                    m: isBackofficeMode ? 0 : 2 / 12,
                  }}
                  width={{ _: 1, m: isBackofficeMode ? 1 : 8 / 12 }}
                  mt={{ _: 0, m: 6 }}
                >
                  <IsActive isActive={Boolean(infoBox)}>
                    <CreatedFrom {...infoBox} />
                  </IsActive>
                  <ErrorFocus />
                  <Recruiter image={job?.captcha_options?.image} mode={mode} />
                  <GeneralInfo
                    isSignedIn={mode !== MODE.PO}
                    allowLinkToOrg={allowLinkToOrg}
                    topicId={topicId}
                  />
                  <Section title={s__('JobForm|Description and attachments')}>
                    <Description />
                    <IsActive isActive={mode !== MODE.PO}>
                      <Uploads
                        jobId={job?.id}
                        mode={mode}
                        networkId={networkId}
                      />
                    </IsActive>
                  </Section>
                  <OtherInfo mode={mode} />
                  <Custom isActive={customAttributesEnabled} />
                  <Apply />
                  <IsActive isActive={isBackofficeMode}>
                    <PublicationInfo />
                  </IsActive>
                  <IsActive isActive={mode !== MODE.BO}>
                    <Actions
                      job={job}
                      mode={mode}
                      networkId={networkId}
                      topicId={topicId}
                    />
                  </IsActive>
                </Col>
              </Row>
            </Grid>
          </Container>
        </Form>
      </Formik>
    </>
  );
};

export default JobFormContainer;
